import React, { useEffect, useState, memo } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  Settings, Users, Music, Image, Video, Newspaper, Radio, 
  Plus, Trash2, Edit, Save, X, Upload, Link as LinkIcon, 
  Shield, Ban, RefreshCw, MessageSquare, Loader2, Sparkles, Globe, Lock,
  Calendar, Send, Check, XCircle, Clock, MapPin, UserPlus, Key, 
  Mail, Eye, EyeOff, Copy, Ticket, ChevronDown, ChevronRight, Crown, UserCog,
  FileText, Play, Download, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  ContextMenu, ContextMenuTrigger, ContextMenuContent,
  ContextMenuItem, ContextMenuSeparator, ContextMenuSub,
  ContextMenuSubTrigger, ContextMenuSubContent
} from '../components/ui/context-menu';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================
// SIMPLE COMPONENTS - DEFINED OUTSIDE
// ============================================

const GlassCard = ({ children, className = '' }) => (
  <div className={`bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl p-3 sm:p-4 ${className}`}>
    {children}
  </div>
);

// Simple input - styled with cyan border like DJSlidin
const AdminInput = ({ value, onChange, className = '', ...props }) => (
  <input
    value={value || ''}
    onChange={onChange}
    className={`flex h-12 w-full rounded-lg border-2 border-cyan-500/50 bg-black/60 px-4 py-2 text-base text-white placeholder:text-gray-500 focus:outline-none focus:border-cyan-500 transition-colors ${className}`}
    autoComplete="off"
    spellCheck="false"
    {...props}
  />
);

// Simple textarea - styled with cyan border like DJSlidin
const AdminTextarea = ({ value, onChange, className = '', ...props }) => (
  <textarea
    value={value || ''}
    onChange={onChange}
    className={`flex w-full rounded-lg border-2 border-cyan-500/50 bg-black/60 px-4 py-3 text-base text-white placeholder:text-gray-500 focus:outline-none focus:border-cyan-500 transition-colors min-h-[100px] resize-none ${className}`}
    autoComplete="off"
    spellCheck="false"
    {...props}
  />
);

// ============================================
// FORM COMPONENTS - DEFINED OUTSIDE
// ============================================

const MusicForm = memo(({ item, onClose, onSave, headers }) => {
  const [form, setForm] = useState(item || {
    title: '', artist: '', description: '', file_url: '', 
    embed_url: '', embed_type: '', cover_image: '', 
    is_trending: false, playlist_id: '', order: 0,
    bpm: '', key: '', genre: '', duration: '', release_date: '',
    album: '', producer: '', featuring: '', tags: [],
    posted_by: 'BeatLab52', // Artist attribution - who is posting this content
    streaming_links: {
      spotify: '', youtube: '', apple_music: '', soundcloud: '',
      tidal: '', audiomack: '', boomplay: '', deezer: '',
      amazon_music: '', bandcamp: '', beatport: ''
    }
  });
  const [uploading, setUploading] = useState(false);
  const [showLinks, setShowLinks] = useState(false);
  const [tagsInput, setTagsInput] = useState(item?.tags?.join(', ') || '');

  // Artist attribution options - both owners share the site
  const postedByOptions = [
    { value: 'BeatLab52', label: 'BeatLab52 (Both)' },
    { value: 'DJ808', label: 'DJ808' },
    { value: 'CRG Productionz', label: 'C.R.G Productionz' }
  ];

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploading(true);
      const res = await axios.post(`${API}/upload/music`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setForm(f => ({...f, file_url: `${process.env.REACT_APP_BACKEND_URL}${res.data.file_url}`}));
      toast.success('File uploaded!');
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...form,
        bpm: form.bpm ? parseInt(form.bpm) : null,
        tags: tagsInput.split(',').map(t => t.trim()).filter(t => t)
      };
      if (item?.id) {
        await axios.put(`${API}/music/${item.id}`, payload, { headers });
        toast.success('Music updated');
      } else {
        await axios.post(`${API}/music`, payload, { headers });
        toast.success('Music added');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving music');
    }
  };

  const updateLink = (platform, value) => {
    setForm(f => ({
      ...f,
      streaming_links: { ...f.streaming_links, [platform]: value }
    }));
  };

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-300">Title *</label>
          <AdminInput value={form.title} onChange={(e) => setForm(f => ({...f, title: e.target.value}))} placeholder="Song title" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-300">Artist *</label>
          <AdminInput value={form.artist} onChange={(e) => setForm(f => ({...f, artist: e.target.value}))} placeholder="Artist name" />
        </div>
      </div>

      {/* Posted By - Artist Attribution */}
      <div>
        <label className="block text-sm font-medium mb-1 text-gray-300">Posted By</label>
        <select
          value={form.posted_by || 'BeatLab52'}
          onChange={(e) => setForm(f => ({...f, posted_by: e.target.value}))}
          className="flex h-12 w-full rounded-lg border-2 border-cyan-500/50 bg-black/60 px-4 py-2 text-base text-white focus:outline-none focus:border-cyan-500 transition-colors"
        >
          {postedByOptions.map(opt => (
            <option key={opt.value} value={opt.value} className="bg-black">{opt.label}</option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">Who is adding this content?</p>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1 text-gray-300">Description</label>
        <AdminTextarea value={form.description} onChange={(e) => setForm(f => ({...f, description: e.target.value}))} placeholder="Description" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-300">Album</label>
          <AdminInput value={form.album} onChange={(e) => setForm(f => ({...f, album: e.target.value}))} placeholder="Album name" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-300">Genre</label>
          <AdminInput value={form.genre} onChange={(e) => setForm(f => ({...f, genre: e.target.value}))} placeholder="Hip-hop, R&B, etc." />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-300">BPM</label>
          <AdminInput type="number" value={form.bpm} onChange={(e) => setForm(f => ({...f, bpm: e.target.value}))} placeholder="120" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-300">Key</label>
          <AdminInput value={form.key} onChange={(e) => setForm(f => ({...f, key: e.target.value}))} placeholder="C minor" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-300">Duration</label>
          <AdminInput value={form.duration} onChange={(e) => setForm(f => ({...f, duration: e.target.value}))} placeholder="3:45" />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1 text-gray-300">Tags (comma separated)</label>
        <AdminInput value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="trap, 808s, dark" />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1 text-gray-300">Audio File</label>
        <div className="flex gap-2">
          <AdminInput value={form.file_url} onChange={(e) => setForm(f => ({...f, file_url: e.target.value}))} placeholder="Audio URL" className="flex-1" />
          <label className="cursor-pointer">
            <input type="file" accept="audio/*" onChange={handleFileUpload} className="hidden" />
            <Button variant="outline" disabled={uploading} asChild>
              <span>{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}</span>
            </Button>
          </label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1 text-gray-300">Cover Image URL</label>
        <AdminInput value={form.cover_image} onChange={(e) => setForm(f => ({...f, cover_image: e.target.value}))} placeholder="https://..." />
      </div>

      <div className="flex items-center gap-2">
        <Switch checked={form.is_trending} onCheckedChange={(c) => setForm(f => ({...f, is_trending: c}))} />
        <label className="text-sm text-gray-300">Trending</label>
      </div>

      {/* Streaming Links Toggle */}
      <Button variant="outline" onClick={() => setShowLinks(!showLinks)} className="w-full">
        <LinkIcon className="w-4 h-4 mr-2" /> {showLinks ? 'Hide' : 'Show'} Streaming Links
      </Button>

      {showLinks && (
        <div className="grid grid-cols-2 gap-3 p-3 bg-white/5 rounded-lg">
          {Object.keys(form.streaming_links || {}).map(platform => (
            <div key={platform}>
              <label className="block text-xs font-medium mb-1 text-gray-400 capitalize">{platform.replace('_', ' ')}</label>
              <AdminInput 
                value={form.streaming_links?.[platform] || ''} 
                onChange={(e) => updateLink(platform, e.target.value)} 
                placeholder={`${platform} URL`}
                className="h-8 text-sm"
              />
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 pt-4">
        <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
        <Button onClick={handleSubmit} className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-black">
          <Save className="w-4 h-4 mr-2" /> Save
        </Button>
      </div>
    </div>
  );
});

const PhotoForm = memo(({ item, onClose, onSave, headers }) => {
  const [form, setForm] = useState(item || {
    title: '', description: '', image_url: '', category: '',
    artist: '', event_name: '', event_date: '', location: '',
    source_type: 'upload', embed_url: '',
    posted_by: 'BeatLab52',
    related_links: { instagram: '', twitter: '', facebook: '' }
  });
  const [uploading, setUploading] = useState(false);
  const [sourceType, setSourceType] = useState(item?.source_type || 'upload');

  // Artist attribution options
  const postedByOptions = [
    { value: 'BeatLab52', label: 'BeatLab52 (Both)' },
    { value: 'DJ808', label: 'DJ808' },
    { value: 'CRG Productionz', label: 'C.R.G Productionz' }
  ];

  const sourceTypes = [
    { id: 'upload', label: 'Upload Image', icon: Upload },
    { id: 'url', label: 'Image URL', icon: LinkIcon },
    { id: 'instagram', label: 'Instagram', icon: null },
    { id: 'facebook', label: 'Facebook', icon: null },
    { id: 'twitter', label: 'X/Twitter', icon: null },
    { id: 'tiktok', label: 'TikTok', icon: null },
  ];

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, GIF, or WebP');
      return;
    }
    
    // Validate file size (20MB max)
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 20MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploading(true);
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setForm(f => ({...f, image_url: `${process.env.REACT_APP_BACKEND_URL}${res.data.file_url}`}));
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.title) {
      toast.error('Please enter a title');
      return;
    }
    if (!form.image_url && sourceType !== 'instagram' && sourceType !== 'facebook' && sourceType !== 'twitter' && sourceType !== 'tiktok') {
      toast.error('Please upload an image or enter an image URL');
      return;
    }
    
    try {
      const payload = { ...form, source_type: sourceType };
      if (item?.id) {
        await axios.put(`${API}/photos/${item.id}`, payload, { headers });
        toast.success('Photo updated');
      } else {
        await axios.post(`${API}/photos`, payload, { headers });
        toast.success('Photo added');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error('Error saving photo');
    }
  };

  return (
    <div className="space-y-5">
      {/* Source Type Tabs */}
      <div className="flex flex-wrap gap-2">
        {sourceTypes.map((type) => (
          <button
            key={type.id}
            onClick={() => setSourceType(type.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              sourceType === type.id
                ? 'bg-cyan-500 text-black'
                : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
            }`}
          >
            {type.icon && <type.icon className="w-4 h-4" />}
            {type.label}
          </button>
        ))}
      </div>

      {/* Title Input */}
      <div>
        <label className="block text-sm font-medium mb-2 text-gray-300">Title</label>
        <div className="relative">
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm(f => ({...f, title: e.target.value}))}
            placeholder="Give this post a title"
            className="w-full h-12 px-4 rounded-lg bg-black/60 border-2 border-cyan-500/50 text-white placeholder:text-gray-500 focus:outline-none focus:border-cyan-500 transition-colors"
            autoComplete="off"
          />
        </div>
      </div>

      {/* Upload Image Section */}
      {sourceType === 'upload' && (
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-300">Upload Image</label>
          <div className="space-y-3">
            {/* URL Preview */}
            <div className="relative">
              <input
                type="text"
                value={form.image_url}
                readOnly
                placeholder="Image URL will appear here after upload"
                className="w-full h-12 px-4 pr-24 rounded-lg bg-black/60 border-2 border-white/20 text-gray-400 placeholder:text-gray-600 focus:outline-none"
              />
              <label className="absolute right-2 top-1/2 -translate-y-1/2">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={uploading}
                />
                <span className={`px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors ${
                  uploading 
                    ? 'bg-gray-600 text-gray-400' 
                    : 'bg-cyan-500 text-black hover:bg-cyan-400'
                }`}>
                  {uploading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading...
                    </span>
                  ) : (
                    'Upload'
                  )}
                </span>
              </label>
            </div>
            <p className="text-xs text-gray-500">Upload JPG, PNG, GIF, WebP (max 20MB)</p>
            
            {/* Image Preview */}
            {form.image_url && (
              <div className="relative w-full h-48 rounded-lg overflow-hidden border border-white/10">
                <img 
                  src={form.image_url} 
                  alt="Preview" 
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <button
                  onClick={() => setForm(f => ({...f, image_url: ''}))}
                  className="absolute top-2 right-2 p-1 bg-black/60 rounded-full hover:bg-black/80"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Image URL Section */}
      {sourceType === 'url' && (
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-300">Image URL</label>
          <input
            type="text"
            value={form.image_url}
            onChange={(e) => setForm(f => ({...f, image_url: e.target.value}))}
            placeholder="https://example.com/image.jpg"
            className="w-full h-12 px-4 rounded-lg bg-black/60 border-2 border-cyan-500/50 text-white placeholder:text-gray-500 focus:outline-none focus:border-cyan-500 transition-colors"
            autoComplete="off"
          />
          {/* Image Preview for URL */}
          {form.image_url && (
            <div className="mt-3 relative w-full h-48 rounded-lg overflow-hidden border border-white/10">
              <img 
                src={form.image_url} 
                alt="Preview" 
                className="w-full h-full object-cover"
                onError={(e) => { e.target.parentElement.style.display = 'none'; }}
              />
            </div>
          )}
        </div>
      )}

      {/* Social Embed Section */}
      {(sourceType === 'instagram' || sourceType === 'facebook' || sourceType === 'twitter' || sourceType === 'tiktok') && (
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-300">{sourceType.charAt(0).toUpperCase() + sourceType.slice(1)} Post URL</label>
          <input
            type="text"
            value={form.embed_url}
            onChange={(e) => setForm(f => ({...f, embed_url: e.target.value}))}
            placeholder={`https://${sourceType === 'twitter' ? 'x' : sourceType}.com/...`}
            className="w-full h-12 px-4 rounded-lg bg-black/60 border-2 border-cyan-500/50 text-white placeholder:text-gray-500 focus:outline-none focus:border-cyan-500 transition-colors"
            autoComplete="off"
          />
          <p className="text-xs text-gray-500 mt-2">Paste the full URL of the {sourceType} post</p>
        </div>
      )}

      {/* Caption/Description */}
      <div>
        <label className="block text-sm font-medium mb-2 text-gray-300">Caption</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm(f => ({...f, description: e.target.value}))}
          placeholder="Add a caption or description..."
          rows={4}
          className="w-full px-4 py-3 rounded-lg bg-black/60 border-2 border-cyan-500/50 text-white placeholder:text-gray-500 focus:outline-none focus:border-cyan-500 transition-colors resize-none"
          autoComplete="off"
        />
      </div>

      {/* Category and Artist */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-300">Category</label>
          <input
            type="text"
            value={form.category}
            onChange={(e) => setForm(f => ({...f, category: e.target.value}))}
            placeholder="Concert, Studio, etc."
            className="w-full h-12 px-4 rounded-lg bg-black/60 border-2 border-white/20 text-white placeholder:text-gray-500 focus:outline-none focus:border-cyan-500 transition-colors"
            autoComplete="off"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-300">Posted By</label>
          <select
            value={form.posted_by || 'BeatLab52'}
            onChange={(e) => setForm(f => ({...f, posted_by: e.target.value}))}
            className="w-full h-12 px-4 rounded-lg bg-black/60 border-2 border-cyan-500/50 text-white focus:outline-none focus:border-cyan-500 transition-colors"
          >
            {postedByOptions.map(opt => (
              <option key={opt.value} value={opt.value} className="bg-black">{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4">
        <Button variant="outline" onClick={onClose} className="flex-1 h-12 border-white/20 hover:bg-white/5">
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          disabled={uploading}
          className="flex-1 h-12 bg-cyan-500 hover:bg-cyan-400 text-black font-medium"
        >
          <Save className="w-4 h-4 mr-2" /> 
          {item?.id ? 'Update Post' : 'Add Post'}
        </Button>
      </div>
    </div>
  );
});

const VideoForm = memo(({ item, onClose, onSave, headers }) => {
  const [form, setForm] = useState(item || {
    title: '', description: '', video_url: '', embed_url: '',
    thumbnail_url: '', category: '', artist: '', release_date: '',
    posted_by: 'BeatLab52',
    related_links: { youtube: '', vimeo: '' }
  });

  // Artist attribution options
  const postedByOptions = [
    { value: 'BeatLab52', label: 'BeatLab52 (Both)' },
    { value: 'DJ808', label: 'DJ808' },
    { value: 'CRG Productionz', label: 'C.R.G Productionz' }
  ];

  const handleSubmit = async () => {
    try {
      if (item?.id) {
        await axios.put(`${API}/videos/${item.id}`, form, { headers });
        toast.success('Video updated');
      } else {
        await axios.post(`${API}/videos`, form, { headers });
        toast.success('Video added');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error('Error saving video');
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1 text-gray-300">Title *</label>
        <AdminInput value={form.title} onChange={(e) => setForm(f => ({...f, title: e.target.value}))} placeholder="Video title" />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1 text-gray-300">Description</label>
        <AdminTextarea value={form.description} onChange={(e) => setForm(f => ({...f, description: e.target.value}))} placeholder="Description" />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1 text-gray-300">Video/Embed URL *</label>
        <AdminInput value={form.video_url} onChange={(e) => setForm(f => ({...f, video_url: e.target.value}))} placeholder="https://youtube.com/..." />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1 text-gray-300">Thumbnail URL</label>
        <AdminInput value={form.thumbnail_url} onChange={(e) => setForm(f => ({...f, thumbnail_url: e.target.value}))} placeholder="https://..." />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-300">Category</label>
          <AdminInput value={form.category} onChange={(e) => setForm(f => ({...f, category: e.target.value}))} placeholder="Music Video, Live, etc." />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-300">Posted By</label>
          <select
            value={form.posted_by || 'BeatLab52'}
            onChange={(e) => setForm(f => ({...f, posted_by: e.target.value}))}
            className="flex h-12 w-full rounded-lg border-2 border-cyan-500/50 bg-black/60 px-4 py-2 text-base text-white focus:outline-none focus:border-cyan-500 transition-colors"
          >
            {postedByOptions.map(opt => (
              <option key={opt.value} value={opt.value} className="bg-black">{opt.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1 text-gray-300">Artist Name</label>
        <AdminInput value={form.artist} onChange={(e) => setForm(f => ({...f, artist: e.target.value}))} placeholder="Artist name" />
      </div>
      <div className="flex items-center gap-2 pt-2">
        <input
          type="checkbox"
          checked={form.is_featured || false}
          onChange={(e) => setForm(f => ({...f, is_featured: e.target.checked}))}
          className="w-4 h-4 rounded accent-yellow-500"
        />
        <label className="text-sm text-gray-300">Feature on Homepage (max 4)</label>
      </div>
      <div className="flex gap-2 pt-4">
        <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
        <Button onClick={handleSubmit} className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-black">
          <Save className="w-4 h-4 mr-2" /> Save
        </Button>
      </div>
    </div>
  );
});

const NewsForm = memo(({ item, onClose, onSave, headers }) => {
  const [form, setForm] = useState(item || {
    title: '', content: '', excerpt: '', image_url: '',
    category: '', author: '', is_featured: false
  });

  const handleSubmit = async () => {
    try {
      if (item?.id) {
        await axios.put(`${API}/news/${item.id}`, form, { headers });
        toast.success('Article updated');
      } else {
        await axios.post(`${API}/news`, form, { headers });
        toast.success('Article added');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error('Error saving article');
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1 text-gray-300">Title *</label>
        <AdminInput value={form.title} onChange={(e) => setForm(f => ({...f, title: e.target.value}))} placeholder="Article title" />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1 text-gray-300">Excerpt</label>
        <AdminInput value={form.excerpt} onChange={(e) => setForm(f => ({...f, excerpt: e.target.value}))} placeholder="Short summary" />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1 text-gray-300">Content *</label>
        <AdminTextarea value={form.content} onChange={(e) => setForm(f => ({...f, content: e.target.value}))} placeholder="Full article content" className="min-h-[150px]" />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1 text-gray-300">Image URL</label>
        <AdminInput value={form.image_url} onChange={(e) => setForm(f => ({...f, image_url: e.target.value}))} placeholder="https://..." />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-300">Category</label>
          <AdminInput value={form.category} onChange={(e) => setForm(f => ({...f, category: e.target.value}))} placeholder="News, Release, etc." />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-300">Author</label>
          <AdminInput value={form.author} onChange={(e) => setForm(f => ({...f, author: e.target.value}))} placeholder="Author name" />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Switch checked={form.is_featured} onCheckedChange={(c) => setForm(f => ({...f, is_featured: c}))} />
        <label className="text-sm text-gray-300">Featured Article</label>
      </div>
      <div className="flex gap-2 pt-4">
        <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
        <Button onClick={handleSubmit} className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-black">
          <Save className="w-4 h-4 mr-2" /> Save
        </Button>
      </div>
    </div>
  );
});

// Chat Management Component
const ChatManagement = memo(({ headers, theme }) => {
  const [chatConfig, setChatConfig] = useState({ chat_page_enabled: true, radio_page_enabled: true });
  const [messages, setMessages] = useState([]);
  const [bannedUsers, setBannedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, messagesRes, bannedRes] = await Promise.all([
        axios.get(`${API}/chat/config`),
        axios.get(`${API}/chat/messages?limit=100`),
        axios.get(`${API}/chat/banned-users`, { headers })
      ]);
      setChatConfig(configRes.data);
      setMessages(messagesRes.data);
      setBannedUsers(bannedRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = async (chatPage, radioPage) => {
    try {
      await axios.put(`${API}/chat/config`, { chat_page_enabled: chatPage, radio_page_enabled: radioPage }, { headers });
      setChatConfig({ chat_page_enabled: chatPage, radio_page_enabled: radioPage });
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const deleteMessage = async (id) => {
    try {
      await axios.delete(`${API}/chat/messages/${id}`, { headers });
      setMessages(messages.filter(m => m.id !== id));
      toast.success('Deleted');
    } catch (error) {
      toast.error('Failed');
    }
  };

  const clearChat = async () => {
    try {
      await axios.delete(`${API}/chat/messages`, { headers });
      setMessages([]);
      setConfirmClear(false);
      toast.success('Chat cleared');
    } catch (error) {
      toast.error('Failed');
    }
  };

  const banUser = async (nick) => {
    try {
      await axios.post(`${API}/chat/ban/${nick}`, {}, { headers });
      setBannedUsers([...bannedUsers, nick]);
      setMessages(messages.filter(m => m.nickname !== nick));
      toast.success('Banned');
    } catch (error) {
      toast.error('Failed');
    }
  };

  const unbanUser = async (nick) => {
    try {
      await axios.delete(`${API}/chat/ban/${nick}`, { headers });
      setBannedUsers(bannedUsers.filter(n => n !== nick));
      toast.success('Unbanned');
    } catch (error) {
      toast.error('Failed');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>;

  return (
    <div className="space-y-6">
      <GlassCard>
        <h3 className="font-bold mb-4 text-cyan-400 flex items-center gap-2"><MessageSquare className="w-5 h-5" /> Chat Controls</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
            <span>Chat Page</span>
            <Switch checked={chatConfig.chat_page_enabled} onCheckedChange={(c) => updateConfig(c, chatConfig.radio_page_enabled)} />
          </div>
          <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
            <span>Radio Chat</span>
            <Switch checked={chatConfig.radio_page_enabled} onCheckedChange={(c) => updateConfig(chatConfig.chat_page_enabled, c)} />
          </div>
        </div>
        <div className="mt-4">
          {!confirmClear ? (
            <Button variant="outline" onClick={() => setConfirmClear(true)} className="border-red-500/50 text-red-400">
              <Trash2 className="w-4 h-4 mr-2" /> Clear All ({messages.length})
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setConfirmClear(false)}>Cancel</Button>
              <Button onClick={clearChat} className="bg-red-500 text-white">Confirm Clear</Button>
            </div>
          )}
        </div>
      </GlassCard>

      <GlassCard>
        <h3 className="font-bold mb-4 text-yellow-400 flex items-center gap-2"><Ban className="w-5 h-5" /> Banned ({bannedUsers.length})</h3>
        {bannedUsers.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {bannedUsers.map(nick => (
              <div key={nick} className="flex items-center gap-2 px-3 py-1 bg-white/5 rounded">
                <span>{nick}</span>
                <button onClick={() => unbanUser(nick)} className="text-green-400 text-xs">Unban</button>
              </div>
            ))}
          </div>
        ) : <p className="text-gray-500">No banned users</p>}
      </GlassCard>

      <GlassCard>
        <h3 className="font-bold mb-4 text-cyan-400 flex items-center gap-2"><MessageSquare className="w-5 h-5" /> Messages ({messages.length})</h3>
        <ScrollArea className="h-[300px]">
          {messages.map(msg => (
            <div key={msg.id} className="flex items-center justify-between p-2 hover:bg-white/5 rounded group">
              <div>
                <span className="text-cyan-400 font-bold">{msg.nickname}: </span>
                <span className="text-gray-300">{msg.message}</span>
              </div>
              <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                <button onClick={() => deleteMessage(msg.id)} className="p-1 text-red-400"><Trash2 className="w-3 h-3" /></button>
                <button onClick={() => banUser(msg.nickname)} className="p-1 text-yellow-400"><Ban className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
        </ScrollArea>
      </GlassCard>
    </div>
  );
});

// ============================================
// MAIN ADMIN COMPONENT
// ============================================

const Admin = () => {
  const { theme } = useTheme();
  const { token, isAdmin, isOwner, hasPermission, loading, user } = useAuth();
  const adminTabOrder = ['music', 'photos', 'videos', 'news', 'events', 'submissions', 'chat', 'users', 'about', 'site-settings'];
  const permKeyMap = { 'site-settings': 'settings' };
  const firstAllowed = adminTabOrder.find(t => hasPermission(permKeyMap[t] || t)) || 'music';
  const [activeTab, setActiveTab] = useState(firstAllowed);
  
  const [music, setMusic] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [news, setNews] = useState([]);
  const [users, setUsers] = useState([]);
  const [radioConfig, setRadioConfig] = useState(null);
  const [artistProfile, setArtistProfile] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [allProfiles, setAllProfiles] = useState([]);
  const [showNewProfileDialog, setShowNewProfileDialog] = useState(false);
  const [newProfileForm, setNewProfileForm] = useState({ artist_name: '', display_name: '', role: '', tagline: '' });
  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityFilterUser, setActivityFilterUser] = useState('');

  const [showMusicModal, setShowMusicModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Events and Submissions state
  const [events, setEvents] = useState([]);
  const [eventSubmissions, setEventSubmissions] = useState([]);
  const [articleSubmissions, setArticleSubmissions] = useState([]);
  const [submissionsConfig, setSubmissionsConfig] = useState({
    events_submissions_enabled: true,
    articles_submissions_enabled: true
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (isAdmin && token) fetchAllData();
  }, [isAdmin, token]);

  // Fetch current user's artist profile
  useEffect(() => {
    if (user?.username && token) {
      fetchArtistProfile(user);
    }
  }, [user?.username, token]);

  const fetchArtistProfile = async (currentUser) => {
    if (!currentUser?.username) return;
    try {
      const profiles = await axios.get(`${API}/artist-profiles`);
      setAllProfiles(profiles.data || []);
      const uname = currentUser.username.toLowerCase().replace(/[^a-z0-9]/g, '');
      const myProfile = profiles.data.find(p => {
        const aname = (p.artist_name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const dname = (p.display_name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        return aname === uname || dname === uname || 
               aname.includes(uname) || dname.includes(uname) ||
               uname.includes(aname) || uname.includes(dname);
      });
      if (myProfile) {
        setArtistProfile(myProfile);
      } else {
        setArtistProfile({
          artist_name: currentUser.username,
          display_name: currentUser.username,
          tagline: '',
          bio: '',
          image_url: '',
          role: '',
          youtube_url: '',
          instagram_url: '',
          facebook_url: '',
          twitter_url: '',
          soundcloud_url: '',
          spotify_url: '',
          website_url: '',
          genres: []
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const switchToProfile = (profile) => {
    setArtistProfile({ ...profile });
  };

  const deleteProfile = async (name) => {
    if (!window.confirm(`Delete profile "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/artist-profiles/${encodeURIComponent(name)}`, { headers });
      toast.success('Profile deleted');
      fetchArtistProfile(user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete profile');
    }
  };

  const createNewProfile = async () => {
    if (!newProfileForm.artist_name || !newProfileForm.display_name) {
      toast.error('Artist name and display name are required');
      return;
    }
    try {
      await axios.post(`${API}/artist-profiles`, {
        artist_name: newProfileForm.artist_name,
        display_name: newProfileForm.display_name,
        role: newProfileForm.role || '',
        tagline: newProfileForm.tagline || '',
        bio: '',
        genres: [],
      }, { headers });
      toast.success('Profile created');
      setShowNewProfileDialog(false);
      fetchArtistProfile(user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    }
  };

  const fetchActivityLog = async (userId = '') => {
    setActivityLoading(true);
    try {
      const url = userId ? `${API}/admin/activity-log?user_id=${userId}` : `${API}/admin/activity-log`;
      const res = await axios.get(url, { headers });
      setActivityLog(res.data || []);
    } catch (error) {
      console.error('Error fetching activity log:', error);
    } finally {
      setActivityLoading(false);
    }
  };

  const saveArtistProfile = async () => {
    if (!artistProfile) return;
    
    setProfileSaving(true);
    try {
      await axios.put(`${API}/artist-profiles/${encodeURIComponent(artistProfile.artist_name)}`, artistProfile, { headers });
      toast.success('Profile updated successfully!');
      const profiles = await axios.get(`${API}/artist-profiles`);
      setAllProfiles(profiles.data || []);
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setProfileSaving(false);
    }
  };

  // Domain Settings State
  const [domainSettings, setDomainSettings] = useState({
    custom_domain: '',
    https_redirect: true,
    ssl_enabled: true
  });
  const [domainSaving, setDomainSaving] = useState(false);

  // Fetch domain settings
  useEffect(() => {
    if (isAdmin && token) {
      fetchDomainSettings();
    }
  }, [isAdmin, token]);

  const fetchDomainSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/domain-settings`, { headers });
      setDomainSettings({
        custom_domain: res.data.custom_domain || '',
        https_redirect: res.data.https_redirect ?? true,
        ssl_enabled: res.data.ssl_enabled ?? true
      });
    } catch (error) {
      console.error('Error fetching domain settings:', error);
    }
  };

  const saveDomainSettings = async () => {
    setDomainSaving(true);
    try {
      await axios.put(`${API}/admin/domain-settings`, null, {
        headers,
        params: {
          custom_domain: domainSettings.custom_domain,
          https_redirect: domainSettings.https_redirect,
          ssl_enabled: domainSettings.ssl_enabled
        }
      });
      toast.success('Domain settings saved!');
    } catch (error) {
      toast.error('Failed to save domain settings');
    } finally {
      setDomainSaving(false);
    }
  };

  // Security Settings State
  const [securitySettings, setSecuritySettings] = useState({
    ddos_protection: true,
    rate_limiting: true,
    rate_limit_requests: 100,
    firewall_enabled: true,
    bot_protection: true,
    brute_force_protection: true,
    max_login_attempts: 5,
    lockout_duration_minutes: 15,
    blocked_ips_count: 0,
    threats_blocked_today: 0
  });
  const [securitySaving, setSecuritySaving] = useState(false);
  const [newBlockedIp, setNewBlockedIp] = useState('');
  const [blockedIps, setBlockedIps] = useState([]);

  // Fetch security settings
  useEffect(() => {
    if (isAdmin && token) {
      fetchSecuritySettings();
    }
  }, [isAdmin, token]);

  const fetchSecuritySettings = async () => {
    try {
      const [settingsRes, ipsRes] = await Promise.all([
        axios.get(`${API}/admin/security-settings`, { headers }),
        axios.get(`${API}/admin/security/blocked-ips`, { headers })
      ]);
      setSecuritySettings(settingsRes.data);
      setBlockedIps(ipsRes.data || []);
    } catch (error) {
      console.error('Error fetching security settings:', error);
    }
  };

  const saveSecuritySettings = async () => {
    setSecuritySaving(true);
    try {
      await axios.put(`${API}/admin/security-settings`, securitySettings, { headers });
      toast.success('Security settings saved!');
    } catch (error) {
      toast.error('Failed to save security settings');
    } finally {
      setSecuritySaving(false);
    }
  };

  const blockIpAddress = async () => {
    if (!newBlockedIp.trim()) return;
    try {
      await axios.post(`${API}/admin/security/block-ip`, null, {
        headers,
        params: { ip: newBlockedIp.trim(), reason: 'Manual block from admin panel' }
      });
      toast.success(`IP ${newBlockedIp} blocked`);
      setNewBlockedIp('');
      fetchSecuritySettings();
    } catch (error) {
      toast.error('Failed to block IP');
    }
  };

  const unblockIpAddress = async (ip) => {
    try {
      await axios.delete(`${API}/admin/security/unblock-ip/${ip}`, { headers });
      toast.success(`IP ${ip} unblocked`);
      fetchSecuritySettings();
    } catch (error) {
      toast.error('Failed to unblock IP');
    }
  };

  // Site Config State (Hero content)
  const [siteConfig, setSiteConfig] = useState({
    site_title: 'BeatLab52',
    site_subtitle: 'DJ808 & CRG Productionz',
    site_tagline: 'Where Beats Come Alive',
    hero_background_url: ''
  });
  const [siteConfigSaving, setSiteConfigSaving] = useState(false);

  // Fetch site config
  useEffect(() => {
    fetchSiteConfig();
  }, []);

  const fetchSiteConfig = async () => {
    try {
      const res = await axios.get(`${API}/site-config`);
      setSiteConfig({
        site_title: res.data.site_title || 'BeatLab52',
        site_subtitle: res.data.site_subtitle || 'DJ808 & CRG Productionz',
        site_tagline: res.data.site_tagline || 'Where Beats Come Alive',
        hero_background_url: res.data.hero_background_url || ''
      });
    } catch (error) {
      console.error('Error fetching site config:', error);
    }
  };

  const saveSiteConfig = async () => {
    setSiteConfigSaving(true);
    try {
      await axios.put(`${API}/admin/site-config`, siteConfig, { headers });
      toast.success('Site content updated!');
    } catch (error) {
      toast.error('Failed to save site content');
    } finally {
      setSiteConfigSaving(false);
    }
  };

  // Seed Articles
  const [seeding, setSeeding] = useState(false);
  const seedArticles = async () => {
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/admin/seed-default-articles`, {}, { headers });
      toast.success(res.data.message || 'Articles seeded successfully!');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed articles');
    } finally {
      setSeeding(false);
    }
  };

  const [clearing, setClearing] = useState(false);
  const clearCache = async () => {
    setClearing(true);
    try {
      localStorage.removeItem('beatlab52_notified_tasks');
      localStorage.removeItem('beatlab52_studio_refreshed');
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('notified_') || key.startsWith('studio_notified_') || key.startsWith('studio_refreshed_')) {
          sessionStorage.removeItem(key);
        }
      });
      const res = await axios.post(`${API}/admin/clear-cache`, {}, { headers });
      toast.success(res.data.message || 'Cache cleared!');
    } catch (error) {
      toast.error('Failed to clear cache');
    } finally {
      setClearing(false);
    }
  };

  // Optimize Site
  const [optimizing, setOptimizing] = useState(false);
  const optimizeSite = async () => {
    setOptimizing(true);
    try {
      const res = await axios.post(`${API}/admin/optimize`, {}, { headers });
      toast.success(res.data.message || 'Site optimized successfully!');
    } catch (error) {
      toast.error('Failed to optimize site');
    } finally {
      setOptimizing(false);
    }
  };

  const fetchAllData = async () => {
    try {
      const [musicRes, photosRes, videosRes, newsRes, usersRes, radioRes, eventsRes, eventSubsRes, articleSubsRes, subsConfigRes] = await Promise.all([
        axios.get(`${API}/music?limit=100`),
        axios.get(`${API}/photos?limit=100`),
        axios.get(`${API}/videos?limit=100`),
        axios.get(`${API}/news?limit=100`),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/radio/config`),
        axios.get(`${API}/events?status=`),
        axios.get(`${API}/admin/submissions/events`, { headers }),
        axios.get(`${API}/admin/submissions/articles`, { headers }),
        axios.get(`${API}/submissions/config`),
      ]);
      setMusic(musicRes.data);
      setPhotos(photosRes.data);
      setVideos(videosRes.data);
      setNews(newsRes.data);
      setUsers(usersRes.data);
      setRadioConfig(radioRes.data);
      setEvents(eventsRes.data);
      setEventSubmissions(eventSubsRes.data.filter(e => e.status === 'pending'));
      setArticleSubmissions(articleSubsRes.data.filter(a => a.status === 'pending'));
      setSubmissionsConfig(subsConfigRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const deleteItem = async (type, id) => {
    if (!confirm('Delete this item?')) return;
    try {
      await axios.delete(`${API}/${type}/${id}`, { headers });
      toast.success('Deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const updateRadioConfig = async () => {
    try {
      await axios.put(`${API}/radio/config`, radioConfig, { headers });
      toast.success('Radio config saved');
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const toggleUserBan = async (userId, isBanned) => {
    try {
      if (isBanned) {
        await axios.delete(`${API}/admin/users/${userId}/ban`, { headers });
      } else {
        await axios.post(`${API}/admin/users/${userId}/ban`, {}, { headers });
      }
      toast.success(isBanned ? 'User unbanned' : 'User banned');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to permanently delete this user?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers });
      toast.success('User deleted');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const editUserAccount = async (userId, data) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/edit`, data, { headers });
      toast.success('Account updated');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update account');
    }
  };

  const resetUserPassword = async (userId, newPassword) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/reset-password`, { new_password: newPassword }, { headers });
      toast.success('Password reset successfully. User sessions have been cleared.');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const createUser = async (data) => {
    try {
      await axios.post(`${API}/admin/users/create`, data, { headers });
      toast.success('User created');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ username: '', email: '' });
  const [resetPasswordForm, setResetPasswordForm] = useState({ password: '', confirm: '' });
  const [showResetPassword, setShowResetPassword] = useState(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({ username: '', email: '', password: '', role: 'user' });
  const [showInvitations, setShowInvitations] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [registrationLocked, setRegistrationLocked] = useState(false);
  const [inviteForm, setInviteForm] = useState({ max_uses: 1, expires_days: 7, note: '' });
  const [expandedUser, setExpandedUser] = useState(null);

  const fetchInvitations = async () => {
    try {
      const res = await axios.get(`${API}/admin/invitations`, { headers });
      setInvitations(res.data);
    } catch (e) {}
  };

  const fetchRegistrationConfig = async () => {
    try {
      const res = await axios.get(`${API}/admin/registration-config`, { headers });
      setRegistrationLocked(res.data.locked);
    } catch (e) {}
  };

  useEffect(() => {
    if (isAdmin && token) {
      fetchInvitations();
      fetchRegistrationConfig();
    }
  }, [isAdmin, token]);

  // Event and Submission Management
  const approveEvent = async (eventId) => {
    try {
      await axios.put(`${API}/admin/submissions/events/${eventId}/approve`, {}, { headers });
      toast.success('Event approved!');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to approve event');
    }
  };

  const rejectEvent = async (eventId) => {
    try {
      await axios.put(`${API}/admin/submissions/events/${eventId}/reject`, {}, { headers });
      toast.success('Event rejected');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to reject event');
    }
  };

  const approveArticle = async (articleId) => {
    try {
      await axios.put(`${API}/admin/submissions/articles/${articleId}/approve`, {}, { headers });
      toast.success('Article approved and published!');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to approve article');
    }
  };

  const rejectArticle = async (articleId) => {
    try {
      await axios.put(`${API}/admin/submissions/articles/${articleId}/reject`, {}, { headers });
      toast.success('Article rejected');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to reject article');
    }
  };

  const toggleSubmissions = async (type, enabled) => {
    try {
      const params = type === 'events' 
        ? { events_enabled: enabled, articles_enabled: submissionsConfig.articles_submissions_enabled }
        : { events_enabled: submissionsConfig.events_submissions_enabled, articles_enabled: enabled };
      
      await axios.put(`${API}/admin/submissions/config`, null, { headers, params });
      setSubmissionsConfig({
        events_submissions_enabled: params.events_enabled,
        articles_submissions_enabled: params.articles_enabled
      });
      toast.success(`${type} submissions ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>;
  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div className="py-4">
      <div className="max-w-5xl mx-auto px-3 sm:px-4">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-xl sm:text-2xl font-black uppercase tracking-wider text-cyan-400" style={{ textShadow: theme.glow }}>
            <Settings className="w-5 h-5 sm:w-6 sm:h-6 inline mr-2" />Admin Panel
          </h1>
          <Link to="/admin/ai-studio">
            <Button className="bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-xs sm:text-sm px-3 py-1.5">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI Studio
            </Button>
          </Link>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap gap-1.5 bg-black/40 p-2 rounded-xl mb-6 h-auto">
            {hasPermission('music') && <TabsTrigger value="music" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 data-[state=active]:border-cyan-500/50 border border-transparent px-3 py-1.5 text-xs"><Music className="w-3.5 h-3.5 mr-1.5" />Music</TabsTrigger>}
            {hasPermission('photos') && <TabsTrigger value="photos" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 data-[state=active]:border-cyan-500/50 border border-transparent px-3 py-1.5 text-xs"><Image className="w-3.5 h-3.5 mr-1.5" />Photos</TabsTrigger>}
            {hasPermission('videos') && <TabsTrigger value="videos" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 data-[state=active]:border-cyan-500/50 border border-transparent px-3 py-1.5 text-xs"><Video className="w-3.5 h-3.5 mr-1.5" />Videos</TabsTrigger>}
            {hasPermission('news') && <TabsTrigger value="news" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 data-[state=active]:border-cyan-500/50 border border-transparent px-3 py-1.5 text-xs"><Newspaper className="w-3.5 h-3.5 mr-1.5" />News</TabsTrigger>}
            {hasPermission('events') && <TabsTrigger value="events" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 data-[state=active]:border-cyan-500/50 border border-transparent px-3 py-1.5 text-xs"><Calendar className="w-3.5 h-3.5 mr-1.5" />Events</TabsTrigger>}
            {hasPermission('submissions') && <TabsTrigger value="submissions" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 data-[state=active]:border-cyan-500/50 border border-transparent px-3 py-1.5 text-xs"><Send className="w-3.5 h-3.5 mr-1.5" />Submissions</TabsTrigger>}
            {hasPermission('chat') && <TabsTrigger value="chat" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 data-[state=active]:border-cyan-500/50 border border-transparent px-3 py-1.5 text-xs"><MessageSquare className="w-3.5 h-3.5 mr-1.5" />Chat</TabsTrigger>}
            {hasPermission('users') && <TabsTrigger value="users" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 data-[state=active]:border-cyan-500/50 border border-transparent px-3 py-1.5 text-xs"><Users className="w-3.5 h-3.5 mr-1.5" />Users</TabsTrigger>}
            {hasPermission('about') && <TabsTrigger value="about" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 data-[state=active]:border-cyan-500/50 border border-transparent px-3 py-1.5 text-xs"><Edit className="w-3.5 h-3.5 mr-1.5" />About</TabsTrigger>}
            {isOwner && <TabsTrigger value="activity-log" onClick={() => { if (activityLog.length === 0) fetchActivityLog(); }} className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 data-[state=active]:border-cyan-500/50 border border-transparent px-3 py-1.5 text-xs"><FileText className="w-3.5 h-3.5 mr-1.5" />Activity Log</TabsTrigger>}
            {hasPermission('settings') && <TabsTrigger value="site-settings" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 data-[state=active]:border-cyan-500/50 border border-transparent px-3 py-1.5 text-xs"><Settings className="w-3.5 h-3.5 mr-1.5" />Settings</TabsTrigger>}
          </TabsList>

          {/* Music Tab */}
          <TabsContent value="music">
            <GlassCard>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-cyan-400">Music Library</h2>
                <Dialog open={showMusicModal} onOpenChange={setShowMusicModal}>
                  <DialogTrigger asChild>
                    <Button onClick={() => setEditingItem(null)} className="bg-cyan-500 text-black"><Plus className="w-4 h-4 mr-2" />Add Music</Button>
                  </DialogTrigger>
                  <DialogContent className="bg-black/95 border-white/10 max-w-2xl">
                    <DialogHeader><DialogTitle className="text-cyan-400">{editingItem ? 'Edit' : 'Add'} Music</DialogTitle></DialogHeader>
                    <MusicForm item={editingItem} onClose={() => setShowMusicModal(false)} onSave={fetchAllData} headers={headers} />
                  </DialogContent>
                </Dialog>
              </div>
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {music.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10">
                    <div>
                      <p className="font-bold text-white">{item.title}</p>
                      <p className="text-sm text-gray-400">{item.artist}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setEditingItem(item); setShowMusicModal(true); }}><Edit className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteItem('music', item.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>
          </TabsContent>

          {/* Photos Tab */}
          <TabsContent value="photos">
            <GlassCard>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-xl font-bold text-cyan-400">Photo & Social Gallery</h2>
                  <p className="text-sm text-gray-500">Manage images and social media embeds</p>
                </div>
                <Dialog open={showPhotoModal} onOpenChange={setShowPhotoModal}>
                  <DialogTrigger asChild>
                    <Button onClick={() => setEditingItem(null)} className="bg-cyan-500 text-black hover:bg-cyan-400"><Plus className="w-4 h-4 mr-2" />Add Post</Button>
                  </DialogTrigger>
                  <DialogContent className="bg-black/95 border-white/10 max-w-lg">
                    <DialogHeader><DialogTitle className="text-cyan-400">{editingItem ? 'Edit Post' : 'Add New Post'}</DialogTitle></DialogHeader>
                    <ScrollArea className="max-h-[70vh]">
                      <PhotoForm item={editingItem} onClose={() => setShowPhotoModal(false)} onSave={fetchAllData} headers={headers} />
                    </ScrollArea>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {photos.map(item => (
                  <div key={item.id} className="relative group rounded-lg overflow-hidden border border-white/10">
                    {item.source_type && item.source_type !== 'upload' && item.source_type !== 'url' && (
                      <div className="absolute top-2 left-2 z-10 px-2 py-1 bg-cyan-500 text-black text-xs font-medium rounded">
                        {item.source_type.toUpperCase()}
                      </div>
                    )}
                    <img src={item.image_url || '/placeholder-image.png'} alt={item.title} className="w-full h-40 object-cover" onError={(e) => { e.target.src = 'https://via.placeholder.com/300x200?text=No+Image'; }} />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setEditingItem(item); setShowPhotoModal(true); }} className="text-white hover:text-cyan-400"><Edit className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteItem('photos', item.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                    <div className="p-2 bg-black/40">
                      <p className="text-sm text-white truncate">{item.title}</p>
                      {item.category && <p className="text-xs text-gray-500">{item.category}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>
          </TabsContent>

          {/* Videos Tab */}
          <TabsContent value="videos">
            <GlassCard>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-cyan-400">Video Gallery</h2>
                <Dialog open={showVideoModal} onOpenChange={setShowVideoModal}>
                  <DialogTrigger asChild>
                    <Button onClick={() => setEditingItem(null)} className="bg-cyan-500 text-black"><Plus className="w-4 h-4 mr-2" />Add Video</Button>
                  </DialogTrigger>
                  <DialogContent className="bg-black/95 border-white/10">
                    <DialogHeader><DialogTitle className="text-cyan-400">{editingItem ? 'Edit' : 'Add'} Video</DialogTitle></DialogHeader>
                    <VideoForm item={editingItem} onClose={() => setShowVideoModal(false)} onSave={fetchAllData} headers={headers} />
                  </DialogContent>
                </Dialog>
              </div>
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {videos.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10">
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="font-bold text-white">{item.title}</p>
                        <p className="text-sm text-gray-400">{item.category}{item.is_featured ? ' • Featured' : ''}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={async () => {
                        try {
                          await axios.put(`${API}/videos/${item.id}/featured`, {}, { headers });
                          fetchAllData();
                          toast.success(item.is_featured ? 'Removed from featured' : 'Added to featured');
                        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
                      }} className={item.is_featured ? 'text-yellow-400' : 'text-gray-400'}>
                        <Star className={`w-4 h-4 ${item.is_featured ? 'fill-yellow-400' : ''}`} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setEditingItem(item); setShowVideoModal(true); }}><Edit className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteItem('videos', item.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>
          </TabsContent>

          {/* News Tab */}
          <TabsContent value="news">
            <GlassCard>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-cyan-400">News Articles</h2>
                <Dialog open={showNewsModal} onOpenChange={setShowNewsModal}>
                  <DialogTrigger asChild>
                    <Button onClick={() => setEditingItem(null)} className="bg-cyan-500 text-black"><Plus className="w-4 h-4 mr-2" />Add Article</Button>
                  </DialogTrigger>
                  <DialogContent className="bg-black/95 border-white/10 max-w-2xl">
                    <DialogHeader><DialogTitle className="text-cyan-400">{editingItem ? 'Edit' : 'Add'} Article</DialogTitle></DialogHeader>
                    <NewsForm item={editingItem} onClose={() => setShowNewsModal(false)} onSave={fetchAllData} headers={headers} />
                  </DialogContent>
                </Dialog>
              </div>
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {news.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10">
                    <div>
                      <p className="font-bold text-white">{item.title}</p>
                      <p className="text-sm text-gray-400">{item.category} • {item.author}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setEditingItem(item); setShowNewsModal(true); }}><Edit className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteItem('news', item.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>
          </TabsContent>

          {/* Chat Tab */}
          <TabsContent value="chat">
            <ChatManagement headers={headers} theme={theme} />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="space-y-4">
              <GlassCard>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold" style={{ color: theme.primary }}>User Management</h2>
                    <p className="text-sm text-gray-500">{users.length} registered users — right-click any user for actions</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {isOwner && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowInvitations(!showInvitations)}
                          className="text-xs border-white/20 text-gray-300 hover:bg-white/10"
                        >
                          <Ticket className="w-3.5 h-3.5 mr-1" />
                          Invitations
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => { setShowCreateUser(true); setCreateUserForm({ username: '', email: '', password: '', role: 'user' }); }}
                          style={{ backgroundColor: theme.primary, color: '#000' }}
                          className="text-xs font-bold"
                        >
                          <UserPlus className="w-3.5 h-3.5 mr-1" />
                          Create User
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {isOwner && (
                  <div className="flex items-center justify-between px-3 py-2 mb-4 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-center gap-2">
                      {registrationLocked ? <Lock className="w-4 h-4 text-red-400" /> : <Globe className="w-4 h-4 text-green-400" />}
                      <span className="text-sm text-gray-300">Registration {registrationLocked ? 'Locked (Invite Only)' : 'Open'}</span>
                    </div>
                    <Switch
                      checked={registrationLocked}
                      onCheckedChange={async (checked) => {
                        try {
                          await axios.put(`${API}/admin/registration-config`, { locked: checked }, { headers });
                          setRegistrationLocked(checked);
                          toast.success(checked ? 'Registration locked — invite codes required' : 'Registration opened');
                        } catch (e) { toast.error('Failed to update'); }
                      }}
                    />
                  </div>
                )}

                <ScrollArea className="max-h-[60vh]">
                  <div className="space-y-1">
                    {users.map(u => {
                      const roleColor = u.role === 'owner' ? '#FFD700' : u.role === 'admin' ? theme.primary : '#9CA3AF';
                      const roleIcon = u.role === 'owner' ? <Crown className="w-3.5 h-3.5" /> : u.role === 'admin' ? <UserCog className="w-3.5 h-3.5" /> : <Users className="w-3.5 h-3.5" />;
                      const canManage = (isOwner && u.role !== 'owner') || (!isOwner && hasPermission('users') && u.role === 'user');

                      return (
                        <ContextMenu key={u.id}>
                          <ContextMenuTrigger asChild>
                            <div className={`rounded-lg transition-all cursor-context-menu ${expandedUser === u.id ? 'bg-white/10 ring-1' : 'bg-white/5 hover:bg-white/[0.07]'}`} style={expandedUser === u.id ? { ringColor: theme.primary + '40' } : {}}>
                              <div
                                className="flex items-center gap-3 p-3"
                                onClick={() => setExpandedUser(expandedUser === u.id ? null : u.id)}
                              >
                                <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold uppercase flex-shrink-0" style={{ backgroundColor: roleColor + '20', color: roleColor, border: `1px solid ${roleColor}40` }}>
                                  {u.username.charAt(0)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-bold text-white text-sm truncate">{u.username}</span>
                                    <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider" style={{ backgroundColor: roleColor + '20', color: roleColor }}>
                                      {roleIcon}
                                      {u.role}
                                    </span>
                                    {u.is_banned && <span className="text-[10px] px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded-full font-bold">BANNED</span>}
                                  </div>
                                  <p className="text-xs text-gray-500 truncate">{u.email}</p>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {canManage && (
                                    <div className="flex items-center gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); setEditingUser(u); setEditUserForm({ username: u.username, email: u.email }); }}
                                        className="h-7 w-7 p-0 text-gray-500 hover:text-white"
                                        title="Edit Account"
                                      >
                                        <Edit className="w-3.5 h-3.5" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); toggleUserBan(u.id, u.is_banned); }}
                                        className={`h-7 w-7 p-0 ${u.is_banned ? 'text-green-400 hover:text-green-300' : 'text-red-400 hover:text-red-300'}`}
                                        title={u.is_banned ? 'Unban' : 'Ban'}
                                      >
                                        {u.is_banned ? <Shield className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                                      </Button>
                                    </div>
                                  )}
                                  {isOwner && u.role === 'admin' && (
                                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${expandedUser === u.id ? 'rotate-180' : ''}`} />
                                  )}
                                </div>
                              </div>

                              {expandedUser === u.id && isOwner && u.role === 'admin' && u.effective_permissions && (
                                <div className="px-3 pb-3 pt-1 border-t border-white/5">
                                  <p className="text-xs text-gray-500 mb-3 font-medium uppercase tracking-wider">Admin Permissions</p>
                                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                    {Object.entries(u.effective_permissions).map(([key, val]) => (
                                      <div
                                        key={key}
                                        className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-black/30 border border-white/5"
                                      >
                                        <span className="text-xs text-gray-300 capitalize">{key.replace('_', ' ')}</span>
                                        <Switch
                                          checked={val}
                                          onCheckedChange={async (checked) => {
                                            const newPerms = { ...u.effective_permissions, [key]: checked };
                                            try {
                                              await axios.put(`${API}/admin/users/${u.id}/permissions`, { permissions: newPerms }, { headers });
                                              fetchAllData();
                                            } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                                          }}
                                          className="scale-75"
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </ContextMenuTrigger>
                          <ContextMenuContent
                            className="min-w-[200px] rounded-xl border backdrop-blur-xl"
                            style={{ backgroundColor: 'rgba(10, 10, 20, 0.95)', borderColor: theme.primary + '33', boxShadow: `0 0 20px ${theme.primary}1A` }}
                          >
                            <div className="px-3 py-2 border-b border-white/10">
                              <p className="text-sm font-bold text-white">{u.username}</p>
                              <p className="text-xs text-gray-500">{u.email}</p>
                            </div>
                            {canManage && (
                              <>
                                <ContextMenuItem
                                  onClick={() => { setEditingUser(u); setEditUserForm({ username: u.username, email: u.email }); }}
                                  className="flex items-center gap-2 text-gray-200 hover:text-white focus:bg-white/10 focus:text-white cursor-pointer"
                                >
                                  <Edit className="w-4 h-4" style={{ color: theme.primary }} />
                                  Edit Account
                                </ContextMenuItem>
                                <ContextMenuItem
                                  onClick={() => { setShowResetPassword(u); setResetPasswordForm({ password: '', confirm: '' }); }}
                                  className="flex items-center gap-2 text-gray-200 hover:text-white focus:bg-white/10 focus:text-white cursor-pointer"
                                >
                                  <Key className="w-4 h-4 text-yellow-400" />
                                  Reset Password
                                </ContextMenuItem>
                                <ContextMenuSeparator className="bg-white/10" />
                                {isOwner && (
                                  <ContextMenuItem
                                    onClick={async () => {
                                      const newRole = u.role === 'admin' ? 'user' : 'admin';
                                      try {
                                        await axios.put(`${API}/admin/users/${u.id}/role?role=${newRole}`, {}, { headers });
                                        fetchAllData();
                                        toast.success(`${u.username} is now ${newRole}`);
                                      } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                                    }}
                                    className="flex items-center gap-2 text-gray-200 hover:text-white focus:bg-white/10 focus:text-white cursor-pointer"
                                  >
                                    <UserCog className="w-4 h-4 text-purple-400" />
                                    {u.role === 'admin' ? 'Demote to User' : 'Promote to Admin'}
                                  </ContextMenuItem>
                                )}
                                <ContextMenuItem
                                  onClick={() => toggleUserBan(u.id, u.is_banned)}
                                  className="flex items-center gap-2 text-gray-200 hover:text-white focus:bg-white/10 focus:text-white cursor-pointer"
                                >
                                  {u.is_banned ? <Shield className="w-4 h-4 text-green-400" /> : <Ban className="w-4 h-4 text-orange-400" />}
                                  {u.is_banned ? 'Unban User' : 'Ban User'}
                                </ContextMenuItem>
                                <ContextMenuSeparator className="bg-white/10" />
                                <ContextMenuItem
                                  onClick={() => deleteUser(u.id)}
                                  className="flex items-center gap-2 text-red-400 hover:text-red-300 focus:bg-red-500/10 focus:text-red-300 cursor-pointer"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Delete User
                                </ContextMenuItem>
                              </>
                            )}
                            {!canManage && (
                              <div className="px-3 py-2 text-xs text-gray-500">
                                {u.role === 'owner' ? 'Owner accounts cannot be modified' : 'No management actions available'}
                              </div>
                            )}
                          </ContextMenuContent>
                        </ContextMenu>
                      );
                    })}
                  </div>
                </ScrollArea>
              </GlassCard>

              {showInvitations && isOwner && (
                <GlassCard>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold" style={{ color: theme.primary }}>
                      <Ticket className="w-4 h-4 inline mr-2" />
                      Invitation Codes
                    </h3>
                    <Button variant="ghost" size="sm" onClick={() => setShowInvitations(false)} className="text-gray-400 h-7 w-7 p-0">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex gap-2 mb-4 flex-wrap">
                    <input
                      value={inviteForm.note}
                      onChange={e => setInviteForm(p => ({ ...p, note: e.target.value }))}
                      placeholder="Note (optional)"
                      className="flex-1 min-w-[120px] h-9 rounded-md border border-white/20 bg-black/50 px-3 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-1"
                      style={{ focusRingColor: theme.primary }}
                    />
                    <input
                      type="number"
                      value={inviteForm.max_uses}
                      onChange={e => setInviteForm(p => ({ ...p, max_uses: parseInt(e.target.value) || 1 }))}
                      min={1}
                      className="w-20 h-9 rounded-md border border-white/20 bg-black/50 px-3 text-sm text-white text-center focus:outline-none focus:ring-1"
                      title="Max uses"
                    />
                    <input
                      type="number"
                      value={inviteForm.expires_days}
                      onChange={e => setInviteForm(p => ({ ...p, expires_days: parseInt(e.target.value) || 7 }))}
                      min={1}
                      className="w-20 h-9 rounded-md border border-white/20 bg-black/50 px-3 text-sm text-white text-center focus:outline-none focus:ring-1"
                      title="Expires in days"
                    />
                    <Button
                      size="sm"
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/admin/invitations`, inviteForm, { headers });
                          toast.success('Invitation created');
                          fetchInvitations();
                          setInviteForm({ max_uses: 1, expires_days: 7, note: '' });
                        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                      }}
                      style={{ backgroundColor: theme.primary, color: '#000' }}
                      className="text-xs font-bold"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" />
                      Generate
                    </Button>
                  </div>
                  <div className="text-xs text-gray-500 flex gap-4 mb-3">
                    <span>Max Uses: per code</span>
                    <span>Expires: days from now</span>
                  </div>
                  {invitations.length > 0 ? (
                    <div className="space-y-2">
                      {invitations.map(inv => (
                        <div key={inv.id} className="flex items-center justify-between p-2.5 bg-black/30 rounded-lg border border-white/5">
                          <div className="flex items-center gap-3 min-w-0">
                            <code className="text-sm font-mono font-bold px-2 py-1 rounded bg-white/10" style={{ color: theme.primary }}>{inv.code}</code>
                            <div className="min-w-0">
                              {inv.note && <p className="text-xs text-gray-400 truncate">{inv.note}</p>}
                              <p className="text-[10px] text-gray-600">
                                {inv.uses}/{inv.max_uses} used
                                {inv.expires_at && ` — expires ${new Date(inv.expires_at).toLocaleDateString()}`}
                                {inv.used_by && ` — ${inv.used_by.replace(/;$/, '')}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { navigator.clipboard.writeText(inv.code); toast.success('Code copied'); }}
                              className="h-7 w-7 p-0 text-gray-500 hover:text-white"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                try {
                                  await axios.delete(`${API}/admin/invitations/${inv.id}`, { headers });
                                  toast.success('Invitation deleted');
                                  fetchInvitations();
                                } catch (e) { toast.error('Failed'); }
                              }}
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-400"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No invitation codes yet</p>
                  )}
                </GlassCard>
              )}
            </div>

            <Dialog open={!!editingUser} onOpenChange={(open) => { if (!open) setEditingUser(null); }}>
              <DialogContent className="bg-gray-950 border-white/10 text-white max-w-md">
                <DialogHeader>
                  <DialogTitle style={{ color: theme.primary }}>Edit Account — {editingUser?.username}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Username</label>
                    <input
                      value={editUserForm.username}
                      onChange={e => setEditUserForm(p => ({ ...p, username: e.target.value }))}
                      className="w-full h-10 rounded-md border border-white/20 bg-black/50 px-3 text-sm text-white focus:outline-none focus:ring-2"
                      style={{ focusRingColor: theme.primary }}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Email</label>
                    <input
                      type="email"
                      value={editUserForm.email}
                      onChange={e => setEditUserForm(p => ({ ...p, email: e.target.value }))}
                      className="w-full h-10 rounded-md border border-white/20 bg-black/50 px-3 text-sm text-white focus:outline-none focus:ring-2"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="ghost" onClick={() => setEditingUser(null)} className="text-gray-400">Cancel</Button>
                    <Button
                      onClick={async () => {
                        const data = {};
                        if (editUserForm.username !== editingUser.username) data.username = editUserForm.username;
                        if (editUserForm.email !== editingUser.email) data.email = editUserForm.email;
                        if (Object.keys(data).length === 0) { toast.info('No changes made'); return; }
                        await editUserAccount(editingUser.id, data);
                        setEditingUser(null);
                      }}
                      style={{ backgroundColor: theme.primary, color: '#000' }}
                      className="font-bold"
                    >
                      <Save className="w-4 h-4 mr-1" />
                      Save Changes
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={!!showResetPassword} onOpenChange={(open) => { if (!open) setShowResetPassword(null); }}>
              <DialogContent className="bg-gray-950 border-white/10 text-white max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-yellow-400">Reset Password — {showResetPassword?.username}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  <p className="text-xs text-gray-500">This will change the password and log out all active sessions for this user.</p>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">New Password</label>
                    <input
                      type="password"
                      value={resetPasswordForm.password}
                      onChange={e => setResetPasswordForm(p => ({ ...p, password: e.target.value }))}
                      className="w-full h-10 rounded-md border border-white/20 bg-black/50 px-3 text-sm text-white focus:outline-none focus:ring-2"
                      minLength={6}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Confirm Password</label>
                    <input
                      type="password"
                      value={resetPasswordForm.confirm}
                      onChange={e => setResetPasswordForm(p => ({ ...p, confirm: e.target.value }))}
                      className="w-full h-10 rounded-md border border-white/20 bg-black/50 px-3 text-sm text-white focus:outline-none focus:ring-2"
                      minLength={6}
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="ghost" onClick={() => setShowResetPassword(null)} className="text-gray-400">Cancel</Button>
                    <Button
                      onClick={async () => {
                        if (resetPasswordForm.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
                        if (resetPasswordForm.password !== resetPasswordForm.confirm) { toast.error('Passwords do not match'); return; }
                        await resetUserPassword(showResetPassword.id, resetPasswordForm.password);
                        setShowResetPassword(null);
                      }}
                      className="bg-yellow-500 text-black font-bold hover:bg-yellow-400"
                    >
                      <Key className="w-4 h-4 mr-1" />
                      Reset Password
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
              <DialogContent className="bg-gray-950 border-white/10 text-white max-w-md">
                <DialogHeader>
                  <DialogTitle style={{ color: theme.primary }}>Create New User</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Username</label>
                    <input
                      value={createUserForm.username}
                      onChange={e => setCreateUserForm(p => ({ ...p, username: e.target.value }))}
                      className="w-full h-10 rounded-md border border-white/20 bg-black/50 px-3 text-sm text-white focus:outline-none focus:ring-2"
                      placeholder="Enter username"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Email</label>
                    <input
                      type="email"
                      value={createUserForm.email}
                      onChange={e => setCreateUserForm(p => ({ ...p, email: e.target.value }))}
                      className="w-full h-10 rounded-md border border-white/20 bg-black/50 px-3 text-sm text-white focus:outline-none focus:ring-2"
                      placeholder="Enter email"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Password</label>
                    <input
                      type="password"
                      value={createUserForm.password}
                      onChange={e => setCreateUserForm(p => ({ ...p, password: e.target.value }))}
                      className="w-full h-10 rounded-md border border-white/20 bg-black/50 px-3 text-sm text-white focus:outline-none focus:ring-2"
                      placeholder="Minimum 6 characters"
                      minLength={6}
                    />
                  </div>
                  {isOwner && (
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Role</label>
                      <Select value={createUserForm.role} onValueChange={v => setCreateUserForm(p => ({ ...p, role: v }))}>
                        <SelectTrigger className="bg-black/50 border-white/20 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-950 border-white/10">
                          <SelectItem value="user" className="text-white">User</SelectItem>
                          <SelectItem value="admin" className="text-white">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="flex gap-2 justify-end">
                    <Button variant="ghost" onClick={() => setShowCreateUser(false)} className="text-gray-400">Cancel</Button>
                    <Button
                      onClick={async () => {
                        if (!createUserForm.username || !createUserForm.email || !createUserForm.password) { toast.error('All fields required'); return; }
                        if (createUserForm.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
                        await createUser(createUserForm);
                        setShowCreateUser(false);
                      }}
                      style={{ backgroundColor: theme.primary, color: '#000' }}
                      className="font-bold"
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      Create User
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* About Page Tab */}
          <TabsContent value="about">
            <div className="space-y-4">
              {isOwner && allProfiles.length > 0 && (
                <GlassCard>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h2 className="text-lg font-bold" style={{ color: theme.primary }}>All Profiles</h2>
                      <p className="text-xs text-gray-500">Select a profile to edit, or create a new one</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => { setShowNewProfileDialog(true); setNewProfileForm({ artist_name: '', display_name: '', role: '', tagline: '' }); }}
                      style={{ backgroundColor: theme.primary, color: '#000' }}
                      className="text-xs font-bold"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" />
                      Add Profile
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {allProfiles.map(p => (
                      <button
                        key={p.artist_name}
                        onClick={() => switchToProfile(p)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                          artistProfile?.artist_name === p.artist_name
                            ? 'border-opacity-60 bg-opacity-20'
                            : 'border-white/10 bg-white/5 hover:bg-white/10'
                        }`}
                        style={artistProfile?.artist_name === p.artist_name ? { borderColor: theme.primary, backgroundColor: theme.primary + '20', color: theme.primary } : { color: '#d1d5db' }}
                      >
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ backgroundColor: theme.primary + '20', color: theme.primary }}>
                          {(p.display_name || p.artist_name).charAt(0)}
                        </div>
                        <div className="text-left">
                          <span className="font-medium text-xs">{p.display_name || p.artist_name}</span>
                          {p.role && <span className="block text-[10px] text-gray-500 truncate max-w-[120px]">{p.role}</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                </GlassCard>
              )}

              <GlassCard>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold" style={{ color: theme.primary }}>
                      {isOwner ? `Edit: ${artistProfile?.display_name || 'Profile'}` : 'Your Artist Profile'}
                    </h2>
                    <p className="text-sm text-gray-500">Edit profile that appears on the About page</p>
                  </div>
                  {isOwner && artistProfile && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteProfile(artistProfile.artist_name)}
                      className="text-red-400 hover:text-red-300 text-xs"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Delete
                    </Button>
                  )}
                </div>

                {artistProfile ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-300">Display Name</label>
                        <AdminInput 
                          value={artistProfile.display_name} 
                          onChange={(e) => setArtistProfile(p => ({...p, display_name: e.target.value}))}
                          placeholder="Your display name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-300">Title / Role</label>
                        <AdminInput 
                          value={artistProfile.role || ''} 
                          onChange={(e) => setArtistProfile(p => ({...p, role: e.target.value}))}
                          placeholder="DJ / Producer / Sound Engineer"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">Tagline</label>
                      <AdminInput 
                        value={artistProfile.tagline || ''} 
                        onChange={(e) => setArtistProfile(p => ({...p, tagline: e.target.value}))}
                        placeholder="A short tagline for your profile"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">Profile Image URL</label>
                      <AdminInput 
                        value={artistProfile.image_url || ''} 
                        onChange={(e) => setArtistProfile(p => ({...p, image_url: e.target.value}))}
                        placeholder="https://example.com/your-photo.jpg"
                      />
                      {artistProfile.image_url && (
                        <div className="mt-2 w-24 h-24 rounded-lg overflow-hidden">
                          <img src={artistProfile.image_url} alt="Profile" className="w-full h-full object-cover" onError={(e) => e.target.style.display = 'none'} />
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">Biography</label>
                      <AdminTextarea 
                        value={artistProfile.bio || ''} 
                        onChange={(e) => setArtistProfile(p => ({...p, bio: e.target.value}))}
                        placeholder="Tell your story... (Use double line breaks for paragraphs)"
                        className="min-h-[150px]"
                      />
                      <p className="text-xs text-gray-500 mt-1">Tip: Use blank lines between paragraphs for better formatting</p>
                    </div>

                    <div>
                      <h3 className="text-lg font-bold mb-4" style={{ color: theme.primary }}>Social Links</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-300">YouTube</label>
                          <AdminInput 
                            value={artistProfile.youtube_url || ''} 
                            onChange={(e) => setArtistProfile(p => ({...p, youtube_url: e.target.value}))}
                            placeholder="https://youtube.com/@yourchannel"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-300">Instagram</label>
                          <AdminInput 
                            value={artistProfile.instagram_url || ''} 
                            onChange={(e) => setArtistProfile(p => ({...p, instagram_url: e.target.value}))}
                            placeholder="https://instagram.com/yourhandle"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-300">Facebook</label>
                          <AdminInput 
                            value={artistProfile.facebook_url || ''} 
                            onChange={(e) => setArtistProfile(p => ({...p, facebook_url: e.target.value}))}
                            placeholder="https://facebook.com/yourpage"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-300">Twitter/X</label>
                          <AdminInput 
                            value={artistProfile.twitter_url || ''} 
                            onChange={(e) => setArtistProfile(p => ({...p, twitter_url: e.target.value}))}
                            placeholder="https://twitter.com/yourhandle"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-300">SoundCloud</label>
                          <AdminInput 
                            value={artistProfile.soundcloud_url || ''} 
                            onChange={(e) => setArtistProfile(p => ({...p, soundcloud_url: e.target.value}))}
                            placeholder="https://soundcloud.com/yourprofile"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-300">Spotify</label>
                          <AdminInput 
                            value={artistProfile.spotify_url || ''} 
                            onChange={(e) => setArtistProfile(p => ({...p, spotify_url: e.target.value}))}
                            placeholder="https://open.spotify.com/artist/..."
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-300">Website</label>
                          <AdminInput 
                            value={artistProfile.website_url || ''} 
                            onChange={(e) => setArtistProfile(p => ({...p, website_url: e.target.value}))}
                            placeholder="https://yourwebsite.com"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">Genres (comma-separated)</label>
                      <AdminInput 
                        value={artistProfile.genres?.join(', ') || ''} 
                        onChange={(e) => setArtistProfile(p => ({...p, genres: e.target.value.split(',').map(g => g.trim()).filter(g => g)}))}
                        placeholder="Hip-Hop, R&B, Electronic, House"
                      />
                    </div>

                    <div className="flex justify-end pt-4">
                      <Button 
                        onClick={saveArtistProfile}
                        disabled={profileSaving}
                        style={{ backgroundColor: theme.primary, color: '#000' }}
                        className="font-medium px-8"
                      >
                        {profileSaving ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                        ) : (
                          <><Save className="w-4 h-4 mr-2" /> Save Profile</>
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>Loading profile...</p>
                  </div>
                )}
              </GlassCard>
            </div>

            <Dialog open={showNewProfileDialog} onOpenChange={setShowNewProfileDialog}>
              <DialogContent className="bg-gray-950 border-white/10 text-white max-w-md">
                <DialogHeader>
                  <DialogTitle style={{ color: theme.primary }}>Create New Profile</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Artist Name (unique identifier)</label>
                    <input
                      value={newProfileForm.artist_name}
                      onChange={e => setNewProfileForm(p => ({ ...p, artist_name: e.target.value }))}
                      className="w-full h-10 rounded-md border border-white/20 bg-black/50 px-3 text-sm text-white focus:outline-none focus:ring-2"
                      placeholder="e.g. DJ_Producer_Name"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Display Name</label>
                    <input
                      value={newProfileForm.display_name}
                      onChange={e => setNewProfileForm(p => ({ ...p, display_name: e.target.value }))}
                      className="w-full h-10 rounded-md border border-white/20 bg-black/50 px-3 text-sm text-white focus:outline-none focus:ring-2"
                      placeholder="How the name appears on the About page"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Title / Role</label>
                    <input
                      value={newProfileForm.role}
                      onChange={e => setNewProfileForm(p => ({ ...p, role: e.target.value }))}
                      className="w-full h-10 rounded-md border border-white/20 bg-black/50 px-3 text-sm text-white focus:outline-none focus:ring-2"
                      placeholder="e.g. DJ / Producer / Sound Engineer"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Tagline</label>
                    <input
                      value={newProfileForm.tagline}
                      onChange={e => setNewProfileForm(p => ({ ...p, tagline: e.target.value }))}
                      className="w-full h-10 rounded-md border border-white/20 bg-black/50 px-3 text-sm text-white focus:outline-none focus:ring-2"
                      placeholder="A short tagline"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="ghost" onClick={() => setShowNewProfileDialog(false)} className="text-gray-400">Cancel</Button>
                    <Button
                      onClick={createNewProfile}
                      style={{ backgroundColor: theme.primary, color: '#000' }}
                      className="font-bold"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Create Profile
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Activity Log Tab - Owner Only */}
          <TabsContent value="activity-log">
            <GlassCard>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold" style={{ color: theme.primary }}>
                    <FileText className="w-5 h-5 inline mr-2" />
                    Activity Log
                  </h2>
                  <p className="text-sm text-gray-500">View what each user and admin has generated</p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={activityFilterUser}
                    onChange={(e) => { setActivityFilterUser(e.target.value); fetchActivityLog(e.target.value); }}
                    className="h-9 rounded-md border px-3 text-xs bg-black/50 text-white focus:outline-none"
                    style={{ borderColor: 'rgba(255,255,255,0.2)' }}
                  >
                    <option value="">All Users</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.username} ({u.role})</option>
                    ))}
                  </select>
                  <Button size="sm" variant="ghost" onClick={() => fetchActivityLog(activityFilterUser)}>
                    <RefreshCw className={`w-4 h-4 ${activityLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>

              {activityLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" style={{ color: theme.primary }} />
                </div>
              ) : activityLog.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No activity found</p>
                </div>
              ) : (
                <div className="space-y-1.5 max-h-[600px] overflow-y-auto pr-1">
                  {activityLog.map((item, idx) => (
                    <div key={`${item.id}-${idx}`} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition group">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{
                        backgroundColor: item.activity_type === 'ai_generation' ? 'rgba(168,85,247,0.2)' :
                                        item.activity_type === 'music_upload' ? 'rgba(0,240,255,0.2)' : 'rgba(250,204,21,0.2)'
                      }}>
                        {item.activity_type === 'ai_generation' ? <Sparkles className="w-4 h-4 text-purple-400" /> :
                         item.activity_type === 'music_upload' ? <Music className="w-4 h-4 text-cyan-400" /> :
                         <Clock className="w-4 h-4 text-yellow-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-white truncate">
                            {item.title || item.task_type || 'Untitled'}
                          </span>
                          {item.activity_type === 'ai_task' && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                              item.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                              item.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                              'bg-yellow-500/20 text-yellow-400'
                            }`}>{item.status}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="font-medium" style={{ color: theme.primary }}>
                            {item.created_by || item.username || 'Unknown'}
                          </span>
                          <span>•</span>
                          <span>{item.activity_type === 'ai_generation' ? 'AI Generation' : item.activity_type === 'music_upload' ? 'Music Upload' : 'AI Task'}</span>
                          {item.api && <><span>•</span><span className="uppercase">{item.api}</span></>}
                          {item.music_style && <><span>•</span><span>{item.music_style}</span></>}
                          <span>•</span>
                          <span>{new Date(item.created_at).toLocaleString()}</span>
                        </div>
                      </div>
                      {item.audio_url && (
                        <Button size="sm" variant="ghost" className="h-7 px-2 opacity-0 group-hover:opacity-100 transition" onClick={() => { import('../utils/downloadFile').then(m => m.downloadFile(item.audio_url, `${item.title || 'track'}.mp3`)); }}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </GlassCard>
          </TabsContent>

          {/* Site Settings Tab - Domain, Security & Hero Content */}
          <TabsContent value="site-settings">
            <div className="space-y-6">
              {/* Hero Content Section */}
              <GlassCard>
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-cyan-400">Front Page Content</h2>
                  <p className="text-sm text-gray-500">Edit the hero section text displayed on the homepage</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-300">Main Title / Brand Name</label>
                    <AdminInput 
                      value={siteConfig.site_title}
                      onChange={(e) => setSiteConfig(s => ({...s, site_title: e.target.value}))}
                      placeholder="BeatLab52"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-300">Producer / Credit Line</label>
                    <AdminInput 
                      value={siteConfig.site_subtitle}
                      onChange={(e) => setSiteConfig(s => ({...s, site_subtitle: e.target.value}))}
                      placeholder="DJ808601 BLYN & C.R.G. Productionz"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-300">Tagline / Slogan</label>
                    <AdminInput 
                      value={siteConfig.site_tagline}
                      onChange={(e) => setSiteConfig(s => ({...s, site_tagline: e.target.value}))}
                      placeholder="Where Beats Come Alive"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-300">Hero Background Image URL</label>
                    <AdminInput 
                      value={siteConfig.hero_background_url}
                      onChange={(e) => setSiteConfig(s => ({...s, hero_background_url: e.target.value}))}
                      placeholder="https://example.com/hero-image.jpg"
                    />
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button 
                      onClick={saveSiteConfig}
                      disabled={siteConfigSaving}
                      className="bg-cyan-500 hover:bg-cyan-400 text-black font-medium"
                    >
                      {siteConfigSaving ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                      ) : (
                        <><Save className="w-4 h-4 mr-2" /> Save Content</>
                      )}
                    </Button>
                  </div>
                </div>
              </GlassCard>

              {/* Radio Stream Configuration */}
              <GlassCard>
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-cyan-400 flex items-center gap-2">
                    <Radio className="w-5 h-5" />
                    Radio Stream Configuration
                  </h2>
                  <p className="text-sm text-gray-500">Configure your Shoutcast/Icecast radio stream</p>
                </div>

                {radioConfig && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1 text-gray-300">Stream Server IP / Hostname</label>
                        <AdminInput
                          value={radioConfig.ip || ''}
                          onChange={(e) => setRadioConfig(c => ({...c, ip: e.target.value}))}
                          placeholder="radio.beatlab52.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1 text-gray-300">Port</label>
                        <AdminInput
                          value={radioConfig.port || ''}
                          onChange={(e) => setRadioConfig(c => ({...c, port: parseInt(e.target.value) || 0}))}
                          placeholder="8172"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1 text-gray-300">Mountpoint</label>
                        <AdminInput
                          value={radioConfig.mountpoint || ''}
                          onChange={(e) => setRadioConfig(c => ({...c, mountpoint: e.target.value}))}
                          placeholder="/stream"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1 text-gray-300">Station Name</label>
                        <AdminInput
                          value={radioConfig.station_name || ''}
                          onChange={(e) => setRadioConfig(c => ({...c, station_name: e.target.value}))}
                          placeholder="BeatLab52 Radio"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-300">Metadata API URL</label>
                      <AdminInput
                        value={radioConfig.api_url || ''}
                        onChange={(e) => setRadioConfig(c => ({...c, api_url: e.target.value}))}
                        placeholder="https://radio.beatlab52.com/status-json.xsl"
                      />
                      <p className="text-xs text-gray-500 mt-1">For Shoutcast use: http://IP:PORT/stats?sid=1&json=1</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-300">Stream Password (optional)</label>
                      <AdminInput
                        type="password"
                        value={radioConfig.stream_password || ''}
                        onChange={(e) => setRadioConfig(c => ({...c, stream_password: e.target.value}))}
                        placeholder="Leave blank if none"
                      />
                    </div>
                    <div className="flex items-center gap-6 pt-2">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={radioConfig.chat_enabled ?? true}
                          onCheckedChange={(c) => setRadioConfig(rc => ({...rc, chat_enabled: c}))}
                        />
                        <span className="text-sm text-gray-300">Chat Enabled</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={radioConfig.is_live ?? false}
                          onCheckedChange={(c) => setRadioConfig(rc => ({...rc, is_live: c}))}
                        />
                        <span className="text-sm text-gray-300">Currently Live</span>
                      </div>
                    </div>
                    <div className="flex justify-end pt-2">
                      <Button
                        onClick={updateRadioConfig}
                        className="bg-cyan-500 hover:bg-cyan-400 text-black font-medium"
                      >
                        <Save className="w-4 h-4 mr-2" /> Save Radio Config
                      </Button>
                    </div>
                  </div>
                )}
              </GlassCard>

              {/* Site Management Section */}
              <GlassCard>
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-cyan-400">Site Management</h2>
                  <p className="text-sm text-gray-500">Tools for managing and optimizing your site</p>
                </div>

                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  <Button 
                    onClick={seedArticles}
                    disabled={seeding}
                    variant="outline"
                    className="h-14 sm:h-16 flex flex-col items-center justify-center gap-1 border-cyan-500/30 hover:bg-cyan-500/10 text-xs"
                  >
                    {seeding ? (
                      <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
                    ) : (
                      <Newspaper className="w-5 h-5 text-cyan-400" />
                    )}
                    <span>Seed Articles</span>
                  </Button>
                  
                  <Button 
                    onClick={clearCache}
                    disabled={clearing}
                    variant="outline"
                    className="h-14 sm:h-16 flex flex-col items-center justify-center gap-1 border-orange-500/30 hover:bg-orange-500/10 text-xs"
                  >
                    {clearing ? (
                      <Loader2 className="w-5 h-5 animate-spin text-orange-400" />
                    ) : (
                      <RefreshCw className="w-5 h-5 text-orange-400" />
                    )}
                    <span>Clear Cache</span>
                  </Button>
                  
                  <Button 
                    onClick={optimizeSite}
                    disabled={optimizing}
                    variant="outline"
                    className="h-14 sm:h-16 flex flex-col items-center justify-center gap-1 border-green-500/30 hover:bg-green-500/10 text-xs"
                  >
                    {optimizing ? (
                      <Loader2 className="w-5 h-5 animate-spin text-green-400" />
                    ) : (
                      <Sparkles className="w-5 h-5 text-green-400" />
                    )}
                    <span>Optimize</span>
                  </Button>
                </div>
              </GlassCard>

              {/* Domain & Security Section */}
              <GlassCard>
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-cyan-400">Domain & Security</h2>
                  <p className="text-sm text-gray-500">Configure your custom domain and SSL settings</p>
                </div>

                <div className="space-y-6">
                  {/* Custom Domain */}
                  <div className="p-4 rounded-xl bg-black/40 border border-white/10">
                    <div className="flex items-center gap-3 mb-4">
                      <Globe className="w-5 h-5 text-cyan-400" />
                      <h3 className="text-lg font-bold text-white">Custom Domain</h3>
                    </div>
                    <AdminInput 
                      value={domainSettings.custom_domain}
                      onChange={(e) => setDomainSettings(s => ({...s, custom_domain: e.target.value}))}
                      placeholder="beatlab52.com"
                    />
                    <p className="text-xs text-gray-500 mt-2">Enter your domain without http:// or https://</p>
                  </div>

                  {/* HTTPS Redirect */}
                  <div className="p-4 rounded-xl bg-black/40 border border-white/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-green-400" />
                        <div>
                          <h3 className="font-bold text-white">HTTPS Redirect</h3>
                          <p className="text-xs text-gray-500">Force all traffic to HTTPS</p>
                        </div>
                      </div>
                      <Switch
                        checked={domainSettings.https_redirect}
                        onCheckedChange={(checked) => setDomainSettings(s => ({...s, https_redirect: checked}))}
                        className="data-[state=checked]:bg-green-500"
                      />
                    </div>
                  </div>

                  {/* SSL Certificate */}
                  <div className="p-4 rounded-xl bg-black/40 border border-white/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Lock className={`w-5 h-5 ${domainSettings.ssl_enabled ? 'text-green-400' : 'text-red-400'}`} />
                        <div>
                          <h3 className="font-bold text-white">SSL Certificate</h3>
                          <p className="text-xs text-gray-500">Status: {domainSettings.ssl_enabled ? 'Active' : 'Inactive'}</p>
                        </div>
                      </div>
                      <Switch
                        checked={domainSettings.ssl_enabled}
                        onCheckedChange={(checked) => setDomainSettings(s => ({...s, ssl_enabled: checked}))}
                        className="data-[state=checked]:bg-green-500"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button 
                      onClick={saveDomainSettings}
                      disabled={domainSaving}
                      className="bg-cyan-500 hover:bg-cyan-400 text-black font-medium"
                    >
                      {domainSaving ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                      ) : (
                        <><Save className="w-4 h-4 mr-2" /> Save Domain Settings</>
                      )}
                    </Button>
                  </div>
                </div>
              </GlassCard>

              {/* Security & Protection Section */}
              <GlassCard>
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-red-400">Security & Protection</h2>
                  <p className="text-sm text-gray-500">DDoS protection, firewall, and rate limiting settings</p>
                </div>

                {/* Security Stats */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-4 rounded-xl bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-500/30">
                    <p className="text-2xl font-bold text-white">{securitySettings.blocked_ips_count}</p>
                    <p className="text-sm text-gray-400">Blocked IPs</p>
                  </div>
                  <div className="p-4 rounded-xl bg-gradient-to-br from-green-500/20 to-cyan-500/20 border border-green-500/30">
                    <p className="text-2xl font-bold text-white">{securitySettings.threats_blocked_today}</p>
                    <p className="text-sm text-gray-400">Threats Blocked Today</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* DDoS Protection */}
                  <div className="p-4 rounded-xl bg-black/40 border border-white/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Shield className={`w-5 h-5 ${securitySettings.ddos_protection ? 'text-green-400' : 'text-red-400'}`} />
                        <div>
                          <h3 className="font-bold text-white">DDoS Protection</h3>
                          <p className="text-xs text-gray-500">Protect against distributed denial-of-service attacks</p>
                        </div>
                      </div>
                      <Switch
                        checked={securitySettings.ddos_protection}
                        onCheckedChange={(checked) => setSecuritySettings(s => ({...s, ddos_protection: checked}))}
                        className="data-[state=checked]:bg-green-500"
                      />
                    </div>
                  </div>

                  {/* Firewall */}
                  <div className="p-4 rounded-xl bg-black/40 border border-white/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Shield className={`w-5 h-5 ${securitySettings.firewall_enabled ? 'text-green-400' : 'text-red-400'}`} />
                        <div>
                          <h3 className="font-bold text-white">Web Application Firewall</h3>
                          <p className="text-xs text-gray-500">Block malicious requests and SQL injection attempts</p>
                        </div>
                      </div>
                      <Switch
                        checked={securitySettings.firewall_enabled}
                        onCheckedChange={(checked) => setSecuritySettings(s => ({...s, firewall_enabled: checked}))}
                        className="data-[state=checked]:bg-green-500"
                      />
                    </div>
                  </div>

                  {/* Rate Limiting */}
                  <div className="p-4 rounded-xl bg-black/40 border border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <RefreshCw className={`w-5 h-5 ${securitySettings.rate_limiting ? 'text-green-400' : 'text-red-400'}`} />
                        <div>
                          <h3 className="font-bold text-white">Rate Limiting</h3>
                          <p className="text-xs text-gray-500">Limit requests per minute to prevent abuse</p>
                        </div>
                      </div>
                      <Switch
                        checked={securitySettings.rate_limiting}
                        onCheckedChange={(checked) => setSecuritySettings(s => ({...s, rate_limiting: checked}))}
                        className="data-[state=checked]:bg-green-500"
                      />
                    </div>
                    {securitySettings.rate_limiting && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <label className="block text-sm text-gray-400 mb-2">Requests per minute</label>
                        <AdminInput 
                          type="number"
                          value={securitySettings.rate_limit_requests}
                          onChange={(e) => setSecuritySettings(s => ({...s, rate_limit_requests: parseInt(e.target.value) || 100}))}
                          placeholder="100"
                          className="w-32"
                        />
                      </div>
                    )}
                  </div>

                  {/* Bot Protection */}
                  <div className="p-4 rounded-xl bg-black/40 border border-white/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Ban className={`w-5 h-5 ${securitySettings.bot_protection ? 'text-green-400' : 'text-red-400'}`} />
                        <div>
                          <h3 className="font-bold text-white">Bot Protection</h3>
                          <p className="text-xs text-gray-500">Block known malicious bots and crawlers</p>
                        </div>
                      </div>
                      <Switch
                        checked={securitySettings.bot_protection}
                        onCheckedChange={(checked) => setSecuritySettings(s => ({...s, bot_protection: checked}))}
                        className="data-[state=checked]:bg-green-500"
                      />
                    </div>
                  </div>

                  {/* Brute Force Protection */}
                  <div className="p-4 rounded-xl bg-black/40 border border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Lock className={`w-5 h-5 ${securitySettings.brute_force_protection ? 'text-green-400' : 'text-red-400'}`} />
                        <div>
                          <h3 className="font-bold text-white">Brute Force Protection</h3>
                          <p className="text-xs text-gray-500">Lock accounts after failed login attempts</p>
                        </div>
                      </div>
                      <Switch
                        checked={securitySettings.brute_force_protection}
                        onCheckedChange={(checked) => setSecuritySettings(s => ({...s, brute_force_protection: checked}))}
                        className="data-[state=checked]:bg-green-500"
                      />
                    </div>
                    {securitySettings.brute_force_protection && (
                      <div className="mt-3 pt-3 border-t border-white/10 grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-gray-400 mb-2">Max Login Attempts</label>
                          <AdminInput 
                            type="number"
                            value={securitySettings.max_login_attempts}
                            onChange={(e) => setSecuritySettings(s => ({...s, max_login_attempts: parseInt(e.target.value) || 5}))}
                            placeholder="5"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-400 mb-2">Lockout Duration (min)</label>
                          <AdminInput 
                            type="number"
                            value={securitySettings.lockout_duration_minutes}
                            onChange={(e) => setSecuritySettings(s => ({...s, lockout_duration_minutes: parseInt(e.target.value) || 15}))}
                            placeholder="15"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* IP Blocking Section */}
                  <div className="p-4 rounded-xl bg-black/40 border border-white/10">
                    <div className="flex items-center gap-3 mb-4">
                      <Ban className="w-5 h-5 text-red-400" />
                      <h3 className="font-bold text-white">IP Blocklist</h3>
                    </div>
                    <div className="flex gap-2 mb-4">
                      <AdminInput 
                        value={newBlockedIp}
                        onChange={(e) => setNewBlockedIp(e.target.value)}
                        placeholder="Enter IP address to block (e.g., 192.168.1.1)"
                        className="flex-1"
                      />
                      <Button 
                        onClick={blockIpAddress}
                        disabled={!newBlockedIp.trim()}
                        className="bg-red-500 hover:bg-red-600 text-white"
                      >
                        <Ban className="w-4 h-4 mr-2" /> Block
                      </Button>
                    </div>
                    {blockedIps.length > 0 && (
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {blockedIps.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                            <div>
                              <span className="text-white font-mono text-sm">{item.ip}</span>
                              <span className="text-gray-500 text-xs ml-2">({item.reason})</span>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => unblockIpAddress(item.ip)}
                              className="text-green-400 hover:text-green-300"
                            >
                              Unblock
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {blockedIps.length === 0 && (
                      <p className="text-gray-500 text-sm">No IPs currently blocked</p>
                    )}
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button 
                      onClick={saveSecuritySettings}
                      disabled={securitySaving}
                      className="bg-red-500 hover:bg-red-600 text-white font-medium"
                    >
                      {securitySaving ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                      ) : (
                        <><Save className="w-4 h-4 mr-2" /> Save Security Settings</>
                      )}
                    </Button>
                  </div>
                </div>
              </GlassCard>
            </div>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-cyan-400 flex items-center gap-2">
                  <Calendar className="w-5 h-5" /> Events ({events.length})
                </h2>
                <Button onClick={() => { setEditingItem(null); setShowEventModal(true); }} className="bg-cyan-500 hover:bg-cyan-600 text-black">
                  <Plus className="w-4 h-4 mr-2" /> Add Event
                </Button>
              </div>

              <GlassCard>
                <div className="space-y-3">
                  {events.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No events yet</p>
                  ) : events.map(event => (
                    <div key={event.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
                      <div className="flex items-center gap-4">
                        {event.image_url && (
                          <img src={event.image_url} alt="" className="w-16 h-16 rounded-lg object-cover" />
                        )}
                        <div>
                          <h3 className="font-bold text-white">{event.title}</h3>
                          <div className="flex items-center gap-3 text-sm text-gray-400">
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {event.event_date}</span>
                            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {event.venue || event.location}</span>
                            <span className={`px-2 py-0.5 rounded text-xs ${event.status === 'approved' ? 'bg-green-500/20 text-green-400' : event.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>
                              {event.status}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => { setEditingItem(event); setShowEventModal(true); }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteItem('events', event.id)} className="text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </GlassCard>
            </div>
          </TabsContent>

          {/* Submissions Tab */}
          <TabsContent value="submissions">
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-purple-400 flex items-center gap-2">
                <Send className="w-5 h-5" /> User Submissions
              </h2>

              {/* Submission Toggles */}
              <GlassCard>
                <h3 className="font-bold text-white mb-4">Submission Settings</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <div>
                      <p className="font-medium text-white">Event Submissions</p>
                      <p className="text-sm text-gray-500">Allow users to submit events</p>
                    </div>
                    <Switch 
                      checked={submissionsConfig.events_submissions_enabled}
                      onCheckedChange={(c) => toggleSubmissions('events', c)}
                      className="data-[state=checked]:bg-green-500"
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <div>
                      <p className="font-medium text-white">Article Submissions</p>
                      <p className="text-sm text-gray-500">Allow users to submit articles</p>
                    </div>
                    <Switch 
                      checked={submissionsConfig.articles_submissions_enabled}
                      onCheckedChange={(c) => toggleSubmissions('articles', c)}
                      className="data-[state=checked]:bg-green-500"
                    />
                  </div>
                </div>
              </GlassCard>

              {/* Pending Event Submissions */}
              <GlassCard>
                <h3 className="font-bold text-yellow-400 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5" /> Pending Event Submissions ({eventSubmissions.length})
                </h3>
                {eventSubmissions.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No pending event submissions</p>
                ) : (
                  <div className="space-y-3">
                    {eventSubmissions.map(event => (
                      <div key={event.id} className="p-4 bg-white/5 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-bold text-white">{event.title}</h4>
                            <div className="flex items-center gap-3 text-sm text-gray-400 mt-1">
                              <span><Calendar className="w-3 h-3 inline mr-1" />{event.event_date}</span>
                              <span><MapPin className="w-3 h-3 inline mr-1" />{event.venue || event.location}</span>
                            </div>
                            <p className="text-sm text-gray-500 mt-2">{event.description?.slice(0, 150)}...</p>
                            <p className="text-xs text-cyan-400 mt-2">Submitted by: {event.submitter_name} ({event.submitter_email})</p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => approveEvent(event.id)} className="bg-green-500 hover:bg-green-600 text-white">
                              <Check className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => rejectEvent(event.id)} className="text-red-400 border-red-400">
                              <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </GlassCard>

              {/* Pending Article Submissions */}
              <GlassCard>
                <h3 className="font-bold text-yellow-400 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5" /> Pending Article Submissions ({articleSubmissions.length})
                </h3>
                {articleSubmissions.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No pending article submissions</p>
                ) : (
                  <div className="space-y-3">
                    {articleSubmissions.map(article => (
                      <div key={article.id} className="p-4 bg-white/5 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-bold text-white">{article.title}</h4>
                            <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded">{article.category}</span>
                            <p className="text-sm text-gray-500 mt-2">{article.content?.slice(0, 200)}...</p>
                            <p className="text-xs text-cyan-400 mt-2">Submitted by: {article.submitter_name} ({article.submitter_email})</p>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <Button size="sm" onClick={() => approveArticle(article.id)} className="bg-green-500 hover:bg-green-600 text-white">
                              <Check className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => rejectArticle(article.id)} className="text-red-400 border-red-400">
                              <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </GlassCard>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
